import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { callBackend } from '../../utils/api';
import { mapOptimizerPortfolio } from '../../hooks/useOptimizer';
import {
    deleteOptimizerPortfolioAndLoadList,
    isDefiniteOptimizerMutationRejection,
    requireOptimizerPortfolioList,
} from './optimizerPortfolioMutationContract';
import {
    decodeOptimizerV2ConstraintDocument,
    fetchOptimizerV2PortfolioDetail,
    replayOptimizerSavedInputs,
} from '../../utils/optimizerGenericGroupContract';
import {
    optimizerWatchlistRestorationRequired,
    optimizerPortfolioRecoveryAvailable,
    optimizerWatchlistSelectionNeedsResolution,
    useOptimizerWatchlistRestoration,
} from './OptimizerWatchlistRestorationDialog';
import {
    portfolioInfoKey,
    optimizerPortfolioNeedsRefresh,
    getOptimizerContextPresentation,
    optimizerPortfolioStatusTags,
    buildOptimizerChangeRows,
} from './optimizerPortfolioPresentation';

// One owner scopes every selection, receipt-detail read and portfolio mutation.
// The chooser and dialogs render this state without acquiring another authority.
export default function useSavedPortfolioManagement({
    optimalPortfolioOptimizer,
    optimalPortfolioSummary,
    setOptimalPortfolioOptimizer,
    setOptimalPortfolioSummary,
    handleWhatIfPortfolioChange,
    listOfWhatIfPortfolios,
    fetchOptimizerPortfolios,
    setErrorOptimization,
    setShowPortfolioMetrics,
    setOptimizedCalculations,
    dropdownDisplayValue,
    restrictedPortfolioNames,
    clearOptimizedOverlay,
    handleSelectedOptimizedPortfolio = () => {},
    onBeforeSelectPortfolio = null,
    onWatchlistRestorationRequired = null,
    onWatchlistRestorationStaged = null,
    refreshSavedOptimizedPortfolio = null,
    availableBetaSources = null,
    posSizingSyncStatus = null,
    posSizingFreshnessSummary = null,
    onSyncPosSizingDraft = null,
    posSizingSyncDisabled = false,
    performanceSources = null,
}) {
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [notification, setNotification] = useState({
        open: false,
        severity: 'success',
        message: '',
    });
    const [infoItem, setInfoItem] = useState(null);
    const [infoDetail, setInfoDetail] = useState(null);
    const infoDetailRequestRef = useRef(0);
    const infoItemKeyRef = useRef(null);
    const infoPortfolioListRef = useRef(listOfWhatIfPortfolios);
    infoPortfolioListRef.current = listOfWhatIfPortfolios;
    infoItemKeyRef.current = portfolioInfoKey(infoItem);
    const [refreshStatuses, setRefreshStatuses] = useState({});
    const [refreshingPortfolioIds, setRefreshingPortfolioIds] = useState({});
    const [refreshIssueItem, setRefreshIssueItem] = useState(null);
    // One re-optimization at a time: { id, status: 'running' | 'error', message }.
    const [reoptimization, setReoptimization] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [savedInputReplay, setSavedInputReplay] = useState({
        loading: false,
        result: null,
        error: null,
    });
    const fetchOwnerRef = useRef(fetchOptimizerPortfolios);
    const selectionRequestRef = useRef(0);

    const isCurrentFetchOwner = (owner) => fetchOwnerRef.current === owner;

    const infoItemIdRef = useRef(null);
    infoItemIdRef.current = infoItem?.id ?? null;

    useEffect(() => {
        setSavedInputReplay({ loading: false, result: null, error: null });
        setReoptimization((current) => (current?.status === 'running' ? current : null));
    }, [infoItem?.id]);

    useLayoutEffect(() => {
        const ownerChanged = fetchOwnerRef.current !== fetchOptimizerPortfolios;
        fetchOwnerRef.current = fetchOptimizerPortfolios;
        if (!ownerChanged) {
            return () => {
                fetchOwnerRef.current = null;
            };
        }
        setLoading(false);
        setLoadingMessage('');
        setConfirmOpen(false);
        setItemToDelete(null);
        setInfoDetail(null);
        setReoptimization(null);
        setNotification({ open: false, severity: 'success', message: '' });
        setInfoItem(null);
        setRefreshStatuses({});
        setRefreshingPortfolioIds({});
        setRefreshIssueItem(null);
        return () => {
            fetchOwnerRef.current = null;
        };
    }, [fetchOptimizerPortfolios]);

    const visiblePortfolios = useMemo(
        () => (listOfWhatIfPortfolios || [])
            .filter((item) => !restrictedPortfolioNames.includes(item.value))
            .slice()
            .sort((left, right) => {
                const leftIsPosSizingDraft = left?.type === 'wi';
                const rightIsPosSizingDraft = right?.type === 'wi';
                if (leftIsPosSizingDraft !== rightIsPosSizingDraft) {
                    return leftIsPosSizingDraft ? -1 : 1;
                }
                const leftName = String(left?.value || left?.name || '');
                const rightName = String(right?.value || right?.name || '');
                return leftName.localeCompare(rightName, undefined, { sensitivity: 'base' });
            }),
        [listOfWhatIfPortfolios, restrictedPortfolioNames],
    );

    const performanceSelectedValue = performanceSources?.selectedValue ?? null;
    const selectedPortfolio = useMemo(
        () => visiblePortfolios.find((item) => (performanceSelectedValue != null
            ? String(item?.id) === performanceSelectedValue
            : item?.value === dropdownDisplayValue)) || null,
        [dropdownDisplayValue, performanceSelectedValue, visiblePortfolios],
    );
    const selectedPortfolioIdValue = performanceSelectedValue
        ?? (selectedPortfolio?.id != null ? String(selectedPortfolio.id) : '');

    const currentInfoItem = visiblePortfolios.find((item) => String(item.id) === String(infoItem?.id));
    const infoSummaryIsCurrent = Boolean(currentInfoItem)
        && portfolioInfoKey(currentInfoItem) === portfolioInfoKey(infoItem);
    // Comparison evidence can change while the immutable saved detail stays the same.
    const infoPresentationItem = infoSummaryIsCurrent ? currentInfoItem : infoItem;
    const infoItemContextPresentation = infoItem?.type === 'opt'
        ? getOptimizerContextPresentation(infoPresentationItem)
        : null;
    const infoStatusTag = infoItem?.type === 'opt' && infoSummaryIsCurrent
        ? optimizerPortfolioStatusTags(infoPresentationItem)[0] ?? null
        : null;
    const infoChangeRows = useMemo(() => {
        if (!infoItem || infoItem.type !== 'opt') return [];
        return buildOptimizerChangeRows(infoPresentationItem, availableBetaSources);
    }, [availableBetaSources, infoItem, infoPresentationItem]);

    const infoItemMembershipDelta = useMemo(() => {
        if (infoItem?.type === 'opt' && infoItemContextPresentation?.restorationRequired) {
            return {
                summaryParts: [{
                    label: `${infoItemContextPresentation.missingSecurityCount} saved securit${infoItemContextPresentation.missingSecurityCount === 1 ? 'y is' : 'ies are'} missing from the current Watchlist`,
                }],
            };
        }
        return null;
    }, [infoItem, infoItemContextPresentation]);

    const canRestoreInfoItem = Boolean(
        onWatchlistRestorationStaged && optimizerPortfolioRecoveryAvailable(infoItem),
    );
    const restoration = useOptimizerWatchlistRestoration({
        open: canRestoreInfoItem,
        portfolio: infoItem,
        onStaged: (response, recovery) => {
            if (!isCurrentFetchOwner(fetchOptimizerPortfolios)) return;
            setInfoItem(null);
            setMenuOpen(false);
            onWatchlistRestorationStaged?.(response, recovery);
        },
    });
    const restorationDisabled = loading || restoration.loading || !restoration.preview;
    // Recovery stages a reviewed Watchlist change: it needs this page's review hand-off
    // and the saved Watchlist revisions. Say which is missing instead of a silent button.
    const recoveryUnavailableReason = canRestoreInfoItem ? null
        : !onWatchlistRestorationStaged
            ? 'Watchlist recovery isn’t available on this page. Use Risk Overview or the Optimizer.'
            : 'This saved portfolio has no saved Watchlist record to restore from, so its missing securities can’t be recovered automatically.';
    // Missing Watchlist membership must be recovered before a new run.
    const infoRecoveryRequired = Boolean(
        infoItemContextPresentation?.restorationRequired || restoration.recoveryRequired,
    );
    const infoRecoveryChecking = canRestoreInfoItem && !infoRecoveryRequired
        && restoration.loading && !restoration.preview;
    const canReoptimizeInfoItem = Boolean(refreshSavedOptimizedPortfolio)
        && infoPresentationItem?.type === 'opt'
        && !infoPresentationItem?.optimizerMutationBlocked
        && infoPresentationItem?.receiptStatus === 'available';
    const infoReoptimization = String(reoptimization?.id) === String(infoItem?.id)
        ? reoptimization
        : null;

    const handleReplaySavedInputs = async () => {
        const current = visiblePortfolios.find((item) => (
            String(item?.id) === String(infoItem?.id)
        ));
        if (!current || current.type !== 'opt' || savedInputReplay.loading) return;
        setSavedInputReplay({ loading: true, result: null, error: null });
        try {
            const result = await replayOptimizerSavedInputs(current);
            setSavedInputReplay({ loading: false, result, error: null });
        } catch (error) {
            setSavedInputReplay({
                loading: false,
                result: null,
                error: error?.message || 'The saved Optimizer inputs could not be replayed.',
            });
        }
    };

    const deleteWhatIfPortfolio = async (portfolio, operationOwner) => {
        const portfolioId = portfolio?.id;
        let mutationAttempted = false;
        let mutationBarrierHandle = null;
        setLoading(true);
        setLoadingMessage('Deletion in progress…');
        setErrorOptimization(null);
        try {
            const blocked = await fetchOptimizerPortfolios({
                mutation: {
                    mode: 'block',
                    barrier: {
                        portfolioId,
                        portfolioName: portfolio?.value,
                        expected: 'absent',
                    },
                },
            });
            if (!isCurrentFetchOwner(operationOwner)) return false;
            if (blocked?.status !== 'blocked' || !blocked?.barrierHandle) {
                throw new Error('Portfolio deletion could not establish a mutation barrier.');
            }
            mutationBarrierHandle = blocked.barrierHandle;
            mutationAttempted = true;
            await deleteOptimizerPortfolioAndLoadList({
                portfolioId,
                deleteRequest: (id) => callBackend(
                    'delete_what_if_portfolio',
                    'POST',
                    { portfolio_id: id },
                ),
                listRequest: () => {
                    if (!isCurrentFetchOwner(operationOwner)) {
                        throw new Error('Authentication changed during portfolio deletion.');
                    }
                    return fetchOptimizerPortfolios({
                        force: true,
                        mutation: {
                            mode: 'commit-and-reconcile',
                            barrierHandle: blocked.barrierHandle,
                        },
                    });
                },
            });
            return isCurrentFetchOwner(operationOwner);
        } catch (err) {
            console.error('Error deleting what-if portfolio:', err);
            const mutationRejected = (
                mutationAttempted &&
                !err?.optimizerMutationCommitted &&
                isDefiniteOptimizerMutationRejection(err)
            );
            if (mutationRejected) {
                await fetchOptimizerPortfolios({
                    mutation: { mode: 'release', barrierHandle: mutationBarrierHandle },
                });
            } else if (mutationAttempted && !err?.optimizerMutationCommitted) {
                await fetchOptimizerPortfolios({
                    mutation: { mode: 'mark-unknown', barrierHandle: mutationBarrierHandle },
                });
            }
            if (!isCurrentFetchOwner(operationOwner)) {
                return false;
            }
            if (err?.optimizerMutationCommitted) {
                setRefreshStatuses((current) => ({
                    ...current,
                    [portfolioId]: {
                        type: 'error',
                        label: 'Deleted; refresh required',
                        details: err?.message || 'Reload the saved portfolio list before continuing.',
                    },
                }));
                setErrorOptimization(
                    `Portfolio was deleted, but the saved portfolio list could not be refreshed. Reload the list before continuing. ${err?.message || err}`,
                );
            } else if (mutationAttempted && !mutationRejected) {
                setRefreshStatuses((current) => ({
                    ...current,
                    [portfolioId]: {
                        type: 'error',
                        label: 'Outcome unknown; reload required',
                        details: err?.message || 'Reload the application and inspect the portfolio before continuing.',
                    },
                }));
                setErrorOptimization(
                    `The delete outcome could not be confirmed. Do not retry. Reload the application and inspect the saved portfolio before deleting again. ${err?.message || err}`,
                );
            } else {
                setErrorOptimization(`Failed to delete. ${err?.message || err}`);
            }
            return false;
        } finally {
            if (isCurrentFetchOwner(operationOwner)) {
                setLoading(false);
                setLoadingMessage('');
            }
        }
    };

    const requestDelete = (item) => {
        setItemToDelete(item);
        setConfirmOpen(true);
    };

    const openMenu = () => setMenuOpen(true);
    const closeMenu = () => {
        setMenuOpen(false);
        setRefreshStatuses({});
        setRefreshIssueItem(null);
    };

    // Only the Position Sizing draft has a quick action; optimized portfolios
    // are recovered and re-optimized from their details.
    const syncDraft = async (item) => {
        if (refreshingPortfolioIds[item.id]) {
            return;
        }
        if (posSizingSyncDisabled) return;
        if (!onSyncPosSizingDraft) return;
        if (!optimizerPortfolioNeedsRefresh(
            item,
            posSizingSyncStatus,
            posSizingFreshnessSummary,
        )) return;
        setRefreshingPortfolioIds((current) => ({ ...current, [item.id]: true }));
        setRefreshStatuses((current) => ({
            ...current,
            [item.id]: { type: 'pending', label: 'Syncing draft...', details: null },
        }));
        try {
            const result = await onSyncPosSizingDraft();
            if (result?.success !== false) {
                setRefreshStatuses((current) => ({
                    ...current,
                    [item.id]: { type: 'success', label: 'Draft synced!', details: null },
                }));
            } else {
                setRefreshStatuses((current) => ({
                    ...current,
                    [item.id]: {
                        type: 'error',
                        label: 'Sync failed',
                        details: result?.message || 'Unable to sync draft.',
                    },
                }));
            }
        } catch (err) {
            setRefreshStatuses((current) => ({
                ...current,
                [item.id]: {
                    type: 'error',
                    label: 'Sync failed',
                    details: err?.message || 'An unexpected error occurred.',
                },
            }));
        } finally {
            setRefreshingPortfolioIds((current) => ({ ...current, [item.id]: false }));
        }
    };

    // Re-optimize is exactly Load → Run → Save in the Optimizer tab.
    const handleReoptimize = async () => {
        const item = infoPresentationItem;
        if (!item || !refreshSavedOptimizedPortfolio || reoptimization?.status === 'running') return;
        const operationOwner = fetchOwnerRef.current;
        const fail = (message) => {
            if (!isCurrentFetchOwner(operationOwner)) return;
            setReoptimization({ id: item.id, status: 'error', message });
            if (String(infoItemIdRef.current) !== String(item.id)) {
                setNotification({ open: true, severity: 'error', message });
            }
        };
        setReoptimization({ id: item.id, status: 'running' });
        setErrorOptimization(null);
        try {
            const result = await refreshSavedOptimizedPortfolio(item);
            if (!isCurrentFetchOwner(operationOwner)) return;
            if (result?.success || result?.status === 'SUPERSEDED') {
                setReoptimization(null);
                if (result?.success) {
                    setInfoItem((current) => (String(current?.id) === String(item.id) ? null : current));
                    setNotification({
                        open: true,
                        severity: 'success',
                        message: `Portfolio "${item.value}" re-optimized.`,
                    });
                }
                return;
            }
            if (result?.status === 'WATCHLIST_RESTORATION_REQUIRED') {
                // The Watchlist changed after the details opened: recovery comes first.
                setReoptimization(null);
                setInfoItem((current) => (
                    String(current?.id) === String(item.id) ? (result.portfolio ?? current) : current
                ));
                return;
            }
            fail(['COMMITTED_REFRESH_REQUIRED', 'OUTCOME_UNKNOWN'].includes(result?.status)
                ? `${result?.message || 'The saved outcome could not be confirmed.'} Reload before re-optimizing again.`
                : result?.message || 'The portfolio could not be re-optimized.');
        } catch (err) {
            console.error('Error re-optimizing saved portfolio:', err);
            fail(err?.message || 'The portfolio could not be re-optimized.');
        }
    };

    // Details read against a newer Watchlist revision than the list entry's
    // mean the list is stale (the Watchlist was published after it loaded),
    // not that the portfolio is unavailable: the list is refreshed once and
    // the details are shown for its refreshed entry. A different receipt is a
    // real change, never staleness. Retry after a failed list refresh passes
    // `listStale`, so the list is refreshed before the details are read.
    const loadInfoDetail = async (item, { listStale = false, listRefreshed = false } = {}) => {
        const key = portfolioInfoKey(item);
        const requestId = ++infoDetailRequestRef.current;
        const owner = fetchOwnerRef.current;
        const isOpen = () => isCurrentFetchOwner(owner)
            && infoDetailRequestRef.current === requestId && infoItemKeyRef.current === key;
        const isCurrent = () => isOpen()
            && portfolioInfoKey(infoPortfolioListRef.current?.find((entry) => String(entry.id) === String(item.id))) === key;
        const showUnavailable = (error, staleList) => {
            console.error('Error loading saved portfolio details:', error);
            setInfoDetail({ key, loading: false, unavailable: true, listStale: staleList });
        };
        setInfoDetail({ key, loading: true });
        let detail = null;
        let savedAum;
        if (!listStale) {
            try {
                detail = await fetchOptimizerV2PortfolioDetail(item.id, item.portfolioVersion);
                if (!isCurrent()) return;
                if (detail.receipt.receipt_id !== item.receiptId) {
                    throw new Error('The saved portfolio details do not match its receipt.');
                }
                savedAum = decodeOptimizerV2ConstraintDocument(detail.constraint_document)
                    .constraints.static_constraints.aum;
            } catch (error) {
                if (isCurrent()) showUnavailable(error, false);
                return;
            }
            if (detail.compatibility.current_watchlist_revision_id
                === item.compatibility?.current_watchlist_revision_id) {
                setInfoDetail({ key, loading: false, detail, savedAum });
                return;
            }
            if (listRefreshed) {
                showUnavailable(new Error('The refreshed saved portfolio list is already out of date.'), true);
                return;
            }
        }
        try {
            const response = await fetchOptimizerPortfolios({ force: true });
            if (!isOpen()) return;
            const raw = requireOptimizerPortfolioList(response)
                .find((entry) => String(entry.id) === String(item.id));
            const refreshed = raw ? mapOptimizerPortfolio(raw) : null;
            // A deleted portfolio, or a new version or receipt, is a real
            // change: the dialog then asks to refresh its details.
            if (!refreshed || refreshed.portfolioVersion !== item.portfolioVersion
                || refreshed.receiptId !== item.receiptId) return;
            const refreshedKey = portfolioInfoKey(refreshed);
            setInfoItem(refreshed);
            infoItemKeyRef.current = refreshedKey;
            if (detail && detail.compatibility.current_watchlist_revision_id
                === refreshed.compatibility?.current_watchlist_revision_id) {
                setInfoDetail({ key: refreshedKey, loading: false, detail, savedAum });
            } else {
                void loadInfoDetail(refreshed, { listRefreshed: true });
            }
        } catch (error) {
            if (isOpen()) showUnavailable(error, true);
        }
    };
    const openInfoItem = (item) => {
        setInfoItem(item);
        infoItemKeyRef.current = portfolioInfoKey(item);
        if (item.type === 'opt') void loadInfoDetail(item);
    };
    const viewDetails = (item) => {
        setMenuOpen(false);
        openInfoItem(item);
    };

    const handleConfirmDelete = async () => {
        setConfirmOpen(false);
        if (!itemToDelete) return;
        const operationOwner = fetchOwnerRef.current;
        const deletingItem = itemToDelete;
        const ok = await deleteWhatIfPortfolio(deletingItem, operationOwner);

        if (!isCurrentFetchOwner(operationOwner)) {
            return;
        }

        if (ok) {
            if (deletingItem.value === optimalPortfolioOptimizer) {
                clearOptimizedOverlay?.();
                setOptimalPortfolioOptimizer('');
                setShowPortfolioMetrics(false);
                handleSelectedOptimizedPortfolio();
            }
            if (deletingItem.value === optimalPortfolioSummary) {
                setOptimalPortfolioSummary('');
                setOptimizedCalculations?.(null);
                handleSelectedOptimizedPortfolio();
            }
            setNotification({
                open: true,
                severity: 'success',
                message: `Portfolio "${deletingItem.value}" deleted.`,
            });
        }
        setItemToDelete(null);
    };

    const stopIncompatibleSelection = (item) => {
        if (onBeforeSelectPortfolio?.(item) === true) return true;
        if (!optimizerWatchlistSelectionNeedsResolution(item)) return false;
        setMenuOpen(false);
        if (onWatchlistRestorationRequired && optimizerWatchlistRestorationRequired(item)) {
            onWatchlistRestorationRequired(item);
        } else {
            openInfoItem(item);
        }
        return true;
    };

    // A built-in Performance entry is never a saved portfolio: no list refresh,
    // details, deletion, sync or optimizer action applies to it.
    const selectPerformanceEntry = (value) => {
        selectionRequestRef.current += 1;
        setLoading(false);
        setLoadingMessage('');
        setMenuOpen(false);
        performanceSources.onSelectEntry(value);
    };
    // Performance analyzes current saved holdings; the caller reports
    // whether they can be analyzed, so selection never opens Watchlist recovery.
    const stopSelection = (item) => !performanceSources && stopIncompatibleSelection(item);

    const select = async (value) => {
        if (performanceSources?.entries.some((entry) => entry.value === value)) {
            selectPerformanceEntry(value);
            return;
        }
        const requestId = ++selectionRequestRef.current;
        const owner = fetchOwnerRef.current;
        const isCurrentRequest = () => (
            isCurrentFetchOwner(owner) && selectionRequestRef.current === requestId
        );
        setLoading(false);
        setLoadingMessage('');
        const selectedId = value;
        let item = visiblePortfolios.find((portfolio) => String(portfolio?.id) === String(selectedId));
        if (!item || stopSelection(item)) return;
        setMenuOpen(false);

        if (item.type === 'opt') {
            setLoading(true);
            setLoadingMessage('Checking saved portfolio…');
            try {
                // A cached compatible menu item is not permission to load.
                // Refresh through the sole list owner before changing selection,
                // which also updates the attention indicators in every dropdown.
                const response = await fetchOptimizerPortfolios({ force: true, isCurrentRequest });
                if (!isCurrentRequest()) return;
                const raw = requireOptimizerPortfolioList(response).find(
                    (portfolio) => String(portfolio.id) === String(selectedId),
                );
                if (!raw) throw new Error('The saved portfolio is no longer available.');
                item = mapOptimizerPortfolio(raw);
                if (item.receiptStatus !== 'available') {
                    throw new Error('This saved optimizer portfolio is unavailable.');
                }
                if (stopSelection(item)) return;
            } catch (error) {
                if (isCurrentRequest()) {
                    setNotification({ open: true, severity: 'error',
                        message: error?.message || 'The saved portfolio could not be checked. Please try again.' });
                }
                return;
            } finally {
                if (isCurrentRequest()) {
                    setLoading(false);
                    setLoadingMessage('');
                }
            }
        }
        if (performanceSources) {
            performanceSources.onSelectPortfolio(item);
            return;
        }
        handleWhatIfPortfolioChange({
            target: {
                value: [item.id, item.value],
            },
        });
    };

    const closeDetails = () => {
        if (!restoration.staging) setInfoItem(null);
    };
    const setDraftDetailsOpen = (open) => {
        if (!open && infoItem?.type === 'wi') setInfoItem(null);
    };
    const deleteFromDetails = () => {
        requestDelete(infoItem);
        setInfoItem(null);
        setMenuOpen(false);
    };

    return {
        chooser: {
            visiblePortfolios,
            selectedPortfolio,
            selectedPortfolioIdValue,
            performanceSelectedValue,
            loading,
            loadingMessage,
            menuOpen,
            refreshStatuses,
            refreshingPortfolioIds,
        },
        dialogs: {
            confirmOpen,
            itemToDelete,
            notification,
            refreshIssueItem,
            infoItem,
            infoDetail,
            currentInfoItem,
            infoSummaryIsCurrent,
            infoItemContextPresentation,
            infoStatusTag,
            infoChangeRows,
            infoItemMembershipDelta,
            canRestoreInfoItem,
            restoration,
            restorationDisabled,
            recoveryUnavailableReason,
            infoRecoveryRequired,
            infoRecoveryChecking,
            canReoptimizeInfoItem,
            reoptimization,
            infoReoptimization,
            savedInputReplay,
        },
        actions: {
            openMenu,
            closeMenu,
            select,
            selectEntry: selectPerformanceEntry,
            viewDetails,
            openInfoItem,
            loadInfoDetail,
            requestDelete,
            confirmDelete: handleConfirmDelete,
            cancelDelete: () => setConfirmOpen(false),
            closeDetails,
            setDraftDetailsOpen,
            deleteFromDetails,
            syncDraft,
            reoptimize: handleReoptimize,
            replaySavedInputs: handleReplaySavedInputs,
            showRefreshIssue: (issue) => setRefreshIssueItem(issue),
            closeRefreshIssue: () => setRefreshIssueItem(null),
            dismissNotification: () => setNotification((current) => ({ ...current, open: false })),
        },
    };
}
